import type { AiQueryResponse } from '@/types';

function escapeCsvCell(value: string | number | null | undefined): string {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function toCsvRow(values: Array<string | number | null | undefined>): string {
  return values.map(escapeCsvCell).join(';');
}

export function buildAiResponseCsv(response: AiQueryResponse, question?: string): string {
  const sections: string[] = [];

  if (question) {
    sections.push(toCsvRow(['Frage', question]));
    sections.push('');
  }

  sections.push(toCsvRow(['Antwort', response.answer]));
  sections.push('');

  if (response.prisonerResults?.length) {
    sections.push(toCsvRow([
      'Gefangenennummer',
      'Name',
      'Wohnort',
      'Geburtsdatum',
      'Entlassung',
      'Anstalt/Abteilung',
      'Dokumente',
    ]));
    for (const row of response.prisonerResults) {
      sections.push(
        toCsvRow([
          row.prisonerNumber,
          row.displayName,
          row.wohnort,
          row.geburtsdatum ? row.geburtsdatum.slice(0, 10) : '',
          row.entlassdatum ? row.entlassdatum.slice(0, 10) : '',
          [row.anstalt, row.abteilung].filter(Boolean).join(' / '),
          typeof row.meta?.documentCount === 'number' ? row.meta.documentCount : '',
        ])
      );
    }
    sections.push('');
  } else if (response.structuredResult?.length) {
    sections.push(toCsvRow(['Gefangenennummer', 'Name', 'Wohnort', 'Entlassung']));
    for (const row of response.structuredResult) {
      sections.push(
        toCsvRow([
          row.prisonerNumber,
          `${row.lastName}, ${row.firstName}`,
          row.city,
          row.releaseDate?.slice(0, 10) || row.expectedEnd?.slice(0, 10) || '',
        ])
      );
    }
    sections.push('');
  }

  if (response.documentResults?.length) {
    sections.push(toCsvRow(['Datei', 'Gefangene/r', 'Akteneintrag', 'Typ', 'OCR', 'Seiten']));
    for (const row of response.documentResults) {
      sections.push(
        toCsvRow([
          row.filename,
          row.prisonerName || row.prisonerNumber,
          row.recordItemTitle,
          row.recordItemType,
          row.ocrStatus,
          row.pageCount,
        ])
      );
    }
    sections.push('');
  }

  if (response.analysisMeta?.grouping?.length) {
    sections.push(toCsvRow(['Gruppe', 'Anzahl']));
    for (const row of response.analysisMeta.grouping) {
      sections.push(toCsvRow([row.key, row.count]));
    }
    sections.push('');
  } else if (typeof response.analysisMeta?.totalCount === 'number') {
    sections.push(toCsvRow(['Kennzahl', 'Wert']));
    sections.push(toCsvRow(['Trefferzahl', response.analysisMeta.totalCount]));
    sections.push('');
  }

  if (sections.length <= 3 && !response.prisonerResults?.length && !response.documentResults?.length) {
    return `\uFEFF${sections.join('\n').trim()}\n`;
  }

  return `\uFEFF${sections.join('\n').trim()}\n`;
}

export function downloadAiResponseCsv(response: AiQueryResponse, question?: string) {
  const csv = buildAiResponseCsv(response, question);
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `ki-antwort-${timestamp}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
  return true;
}